import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Autocomplete,
    Avatar,
    Box,
    CircularProgress,
    ClickAwayListener,
    IconButton,
    Link as MuiLink,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {Link as RouterLink} from 'react-router';
import {searchKbEntities} from '../../../utils/api/kbEntitySearch';
import {resolveEntityAvatarUrl} from '../../../utils/entityAvatarUrl';
import {getEntityKBUrl} from '../../../utils/entityLinks';
import {TAGS_SECTION_HEADING_SX} from '../tags/EntityTagsSection';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;
const TARGET_AVATAR_SIZE = 22;

const TARGET_BLOCK_SX = {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: '100%',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'background.paper',
    pl: 0.75,
    pr: 1,
    py: 0.5,
    minHeight: 32,
    position: 'relative',
    gap: 0.75,
};

const ADD_BLOCK_SX = {
    ...TARGET_BLOCK_SX,
    cursor: 'pointer',
    userSelect: 'none',
    color: 'text.secondary',
    fontWeight: 600,
    fontSize: '1.1rem',
    lineHeight: 1,
    pl: 1.25,
    pr: 1.25,
    borderStyle: 'dashed',
    bgcolor: 'action.hover',
    transition: 'border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
        borderColor: 'text.primary',
        bgcolor: 'action.selected',
        color: 'text.primary',
    },
};

/**
 * Normalize a target entity ref (QID uppercased; other refs trimmed).
 * @param {unknown} raw
 * @returns {string|null}
 */
export function normalizeEntityRef(raw) {
    if (raw == null) return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;
    if (/^Q\d+$/i.test(trimmed)) {
        return trimmed.toUpperCase();
    }
    return trimmed;
}

/**
 * Normalize API / form targets into display objects.
 * Accepts string refs or `{entityRef, canonicalName?, entityType?}`.
 * Dedupes by normalized {@code entityRef} (first wins).
 *
 * @param {unknown} raw
 * @returns {Array<{entityRef: string, canonicalName: string|null, entityType: string|null}>}
 */
export function normalizeTargets(raw) {
    if (!Array.isArray(raw)) return [];
    const byRef = new Map();
    for (const t of raw) {
        let next = null;
        if (typeof t === 'string') {
            const entityRef = normalizeEntityRef(t);
            if (entityRef) {
                next = {entityRef, canonicalName: null, entityType: null};
            }
        } else if (t && typeof t === 'object') {
            const entityRef = normalizeEntityRef(
                t.entityRef || t.wikidataId || t.entityId || t.id || t.ref || '',
            );
            if (entityRef) {
                next = {
                    entityRef,
                    canonicalName: t.canonicalName || t.entityLabel || t.label || null,
                    entityType: t.entityType || null,
                };
            }
        }
        if (!next) continue;
        const existing = byRef.get(next.entityRef);
        if (!existing) {
            byRef.set(next.entityRef, next);
            continue;
        }
        // Prefer a row that carries a display name / type when merging duplicates.
        byRef.set(next.entityRef, {
            entityRef: next.entityRef,
            canonicalName: existing.canonicalName || next.canonicalName,
            entityType: existing.entityType || next.entityType,
        });
    }
    return Array.from(byRef.values());
}

/** @param {unknown} raw @returns {string[]} */
export function targetsToRefs(raw) {
    return normalizeTargets(raw).map((t) => t.entityRef);
}

function optionToTarget(option) {
    if (!option) return null;
    if (typeof option === 'string') {
        const entityRef = normalizeEntityRef(option);
        return entityRef ? {entityRef, canonicalName: null, entityType: null} : null;
    }
    const entityRef = normalizeEntityRef(
        option.entityRef || option.entityId || option.wikidataId || option.id || '',
    );
    if (!entityRef) return null;
    return {
        entityRef,
        canonicalName: option.canonicalName || option.entityLabel || option.label || null,
        entityType: option.entityType || null,
    };
}

function displayName(target) {
    const name = (target?.canonicalName || '').trim();
    return name || target?.entityRef || '';
}

/** @param {string} label @returns {string} */
function entityInitials(label) {
    const parts = String(label || '')
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2);
    if (!parts.length) return '?';
    return parts.map((w) => w[0].toUpperCase()).join('');
}

/** @param {{entityRef: string}} target @returns {string|null} */
function targetAvatarSrc(target) {
    const ref = target?.entityRef;
    if (!ref) return null;
    return resolveEntityAvatarUrl({
        wikidataId: ref,
        entityId: /^\d+$/.test(ref) ? ref : null,
    });
}

function TargetBlock({target, onDelete, readOnly = false, disabled = false}) {
    const label = displayName(target);
    const avatarSrc = targetAvatarSrc(target);
    return (
        <Box
            sx={{
                ...TARGET_BLOCK_SX,
                pr: !readOnly && onDelete ? 3.5 : 1,
                opacity: disabled ? 0.7 : 1,
                '&:hover .target-remove': {opacity: 1},
            }}
            data-testid="target-block"
            data-entity-ref={target.entityRef}
        >
            <Avatar
                src={avatarSrc || undefined}
                alt=""
                aria-hidden
                data-testid="target-avatar"
                sx={{
                    width: TARGET_AVATAR_SIZE,
                    height: TARGET_AVATAR_SIZE,
                    flexShrink: 0,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    bgcolor: 'action.selected',
                    color: 'text.secondary',
                }}
            >
                {entityInitials(label)}
            </Avatar>
            <MuiLink
                component={RouterLink}
                to={getEntityKBUrl(target.entityRef)}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                onClick={(e) => e.stopPropagation()}
                sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 280,
                }}
            >
                {label}
            </MuiLink>
            {!readOnly && typeof onDelete === 'function' && (
                <IconButton
                    className="target-remove"
                    size="small"
                    aria-label={`Remove ${label}`}
                    disabled={disabled}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDelete();
                    }}
                    sx={{
                        position: 'absolute',
                        right: 2,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        opacity: 0,
                        width: 22,
                        height: 22,
                        p: 0.25,
                        color: 'text.secondary',
                        transition: 'opacity 0.12s ease',
                        '&:hover': {color: 'text.primary', bgcolor: 'action.hover'},
                    }}
                >
                    <CloseIcon sx={{fontSize: 14}}/>
                </IconButton>
            )}
        </Box>
    );
}

/**
 * KB entity targets (Wikidata QIDs / D refs). Distinct from free-form Tags.
 *
 * Editable mode: name blocks with hover-X delete, trailing `+` that opens a
 * search autocomplete which stays open after each add (closes on click outside).
 *
 * @param {Object} props
 * @param {Array<string|{entityRef?:string,canonicalName?:string,entityType?:string}>} [props.value]
 * @param {(targets: Array<{entityRef: string, canonicalName: string|null, entityType: string|null}>) => void} [props.onChange]
 *   — emits full target objects so parents can keep display names (do not collapse to bare refs)
 * @param {boolean} [props.readOnly=false]
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.showHeading=true]
 * @param {string} [props.label='Targets']
 * @param {string} [props.placeholder='Search entities…']
 * @param {string} [props.size='medium']
 */
export default function EntityTargetsInput({
    value = [],
    onChange,
    readOnly = false,
    disabled = false,
    showHeading = true,
    label = 'Targets',
    placeholder = 'Search entities…',
    size = 'medium',
}) {
    const nameCacheRef = useRef(new Map());
    const [nameCacheTick, setNameCacheTick] = useState(0);

    const rememberName = useCallback((entityRef, canonicalName) => {
        const ref = normalizeEntityRef(entityRef);
        if (!ref || !canonicalName) return;
        const prev = nameCacheRef.current.get(ref);
        if (prev === canonicalName) return;
        nameCacheRef.current.set(ref, canonicalName);
        setNameCacheTick((n) => n + 1);
    }, []);

    useEffect(() => {
        for (const t of normalizeTargets(value)) {
            if (t.canonicalName) {
                nameCacheRef.current.set(t.entityRef, t.canonicalName);
            }
        }
    }, [value]);

    const selected = useMemo(() => {
        return normalizeTargets(value).map((t) => {
            if (t.canonicalName) return t;
            const cached = nameCacheRef.current.get(t.entityRef) || null;
            return cached ? {...t, canonicalName: cached} : t;
        });
        // nameCacheTick forces re-read after local adds if a parent still only keeps refs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, nameCacheTick]);

    const selectedRefs = useMemo(
        () => new Set(selected.map((t) => t.entityRef)),
        [selected],
    );

    const [adding, setAdding] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [searching, setSearching] = useState(false);
    const debounceRef = useRef(null);
    const seqRef = useRef(0);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!adding || readOnly) return undefined;
        const q = (inputValue || '').trim();
        if (q.length < 2) {
            setSuggestions([]);
            setSearching(false);
            return undefined;
        }

        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            const seq = ++seqRef.current;
            setSearching(true);
            try {
                const {results} = await searchKbEntities(q, SEARCH_LIMIT);
                if (seq === seqRef.current) {
                    setSuggestions(Array.isArray(results) ? results : []);
                }
            } catch {
                if (seq === seqRef.current) {
                    setSuggestions([]);
                }
            } finally {
                if (seq === seqRef.current) {
                    setSearching(false);
                }
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(debounceRef.current);
    }, [inputValue, adding, readOnly]);

    useEffect(() => {
        if (!adding) return undefined;
        const id = requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, [adding]);

    const emitChange = useCallback((nextTargets) => {
        if (typeof onChange === 'function') {
            onChange(normalizeTargets(nextTargets));
        }
    }, [onChange]);

    const closeAdding = useCallback(() => {
        setAdding(false);
        setInputValue('');
        setSuggestions([]);
        setSearching(false);
    }, []);

    const addTarget = useCallback((option) => {
        const t = optionToTarget(option);
        if (!t || selectedRefs.has(t.entityRef)) return false;
        if (t.canonicalName) {
            rememberName(t.entityRef, t.canonicalName);
        }
        emitChange([...selected, t]);
        setInputValue('');
        setSuggestions([]);
        return true;
    }, [emitChange, rememberName, selected, selectedRefs]);

    const removeTarget = useCallback((entityRef) => {
        emitChange(selected.filter((t) => t.entityRef !== entityRef));
    }, [emitChange, selected]);

    const availableSuggestions = useMemo(
        () => suggestions.filter((opt) => {
            const t = optionToTarget(opt);
            return t && !selectedRefs.has(t.entityRef);
        }),
        [suggestions, selectedRefs],
    );

    const tryAddFromInput = useCallback(() => {
        const q = (inputValue || '').trim();
        if (!q) return;
        const exact = availableSuggestions.find((opt) => {
            const t = optionToTarget(opt);
            return t && (
                t.entityRef.toLowerCase() === q.toLowerCase()
                || (t.canonicalName || '').toLowerCase() === q.toLowerCase()
            );
        });
        const pick = exact || availableSuggestions[0];
        if (pick) addTarget(pick);
    }, [addTarget, availableSuggestions, inputValue]);

    if (readOnly) {
        if (!selected.length) return null;
        return (
            <Stack spacing={1} sx={{width: '100%', minWidth: 0}}>
                {showHeading && (
                    <Typography sx={TAGS_SECTION_HEADING_SX}>Targets</Typography>
                )}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {selected.map((t) => (
                        <TargetBlock key={t.entityRef} target={t} readOnly/>
                    ))}
                </Stack>
            </Stack>
        );
    }

    return (
        <Stack spacing={1} sx={{width: '100%', minWidth: 0}}>
            {showHeading && (
                <Typography sx={TAGS_SECTION_HEADING_SX}>{label}</Typography>
            )}
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                {selected.map((t) => (
                    <TargetBlock
                        key={t.entityRef}
                        target={t}
                        disabled={disabled}
                        onDelete={() => removeTarget(t.entityRef)}
                    />
                ))}

                {adding ? (
                    <ClickAwayListener onClickAway={closeAdding}>
                        <Box sx={{minWidth: 220, flex: '1 1 220px', maxWidth: 360}}>
                            <Autocomplete
                                size={size}
                                fullWidth
                                open
                                autoHighlight
                                disableClearable
                                disablePortal
                                forcePopupIcon={false}
                                disabled={disabled}
                                filterOptions={(opts) => opts}
                                options={availableSuggestions}
                                value={null}
                                inputValue={inputValue}
                                onInputChange={(_, next, reason) => {
                                    if (reason === 'reset') return;
                                    setInputValue(next);
                                }}
                                onChange={(_, option) => {
                                    if (option) addTarget(option);
                                }}
                                onClose={(_, reason) => {
                                    if (reason === 'escape') closeAdding();
                                }}
                                isOptionEqualToValue={(opt, val) => {
                                    const a = optionToTarget(opt);
                                    const b = optionToTarget(val);
                                    return Boolean(a && b && a.entityRef === b.entityRef);
                                }}
                                getOptionLabel={(opt) => {
                                    const t = optionToTarget(opt);
                                    if (!t) return '';
                                    return t.canonicalName
                                        ? `${t.canonicalName} (${t.entityRef})`
                                        : t.entityRef;
                                }}
                                renderOption={(props, option) => {
                                    const {key, ...optionProps} = props;
                                    const id = option.entityId || option.entityRef || '';
                                    const name = option.entityLabel || option.canonicalName || id;
                                    const meta = [
                                        option.local ? 'Custom' : null,
                                        id,
                                        option.description,
                                    ].filter(Boolean).join(' — ');
                                    return (
                                        <li key={key || id} {...optionProps}>
                                            <Stack spacing={0} minWidth={0}>
                                                <Typography variant="body2" noWrap>{name}</Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {meta}
                                                </Typography>
                                            </Stack>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={inputRef}
                                        placeholder={placeholder}
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                                e.preventDefault();
                                                closeAdding();
                                                return;
                                            }
                                            if (e.key === 'Enter') {
                                                // Autocomplete + autoHighlight normally selects via onChange.
                                                // Fallback: add first/exact match when nothing is highlighted yet.
                                                e.preventDefault();
                                                e.stopPropagation();
                                                tryAddFromInput();
                                            }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {searching
                                                        ? <CircularProgress color="inherit" size={16}/>
                                                        : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </ClickAwayListener>
                ) : (
                    <Box
                        component="button"
                        type="button"
                        aria-label="Add target"
                        disabled={disabled}
                        onClick={() => {
                            if (!disabled) setAdding(true);
                        }}
                        sx={{
                            ...ADD_BLOCK_SX,
                            font: 'inherit',
                            ...(disabled ? {opacity: 0.5, pointerEvents: 'none'} : null),
                        }}
                    >
                        +
                    </Box>
                )}
            </Stack>
        </Stack>
    );
}
